import React, { useState } from 'react'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { PAYMENT_ISSUE_MODAL_COPY } from '../lib/billingGrace'

const BILLING_PORTAL_TIMEOUT_MS = 20000

const getStoredSupabaseAccessToken = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
  if (typeof window === 'undefined' || !supabaseUrl) return null

  try {
    const projectRef = new URL(supabaseUrl).hostname.split('.')[0]
    const authStorageValue = window.localStorage.getItem(`sb-${projectRef}-auth-token`)
    if (!authStorageValue) return null

    const session = JSON.parse(authStorageValue)
    return session?.access_token || session?.currentSession?.access_token || null
  } catch (error) {
    console.error('Unable to read Supabase session for billing portal:', error)
    return null
  }
}

/**
 * Shown when a premium subscriber is in the 3-day payment failure grace window.
 */
const PaymentIssueModal = ({ isOpen, onClose }) => {
  const [isOpeningBillingPortal, setIsOpeningBillingPortal] = useState(false)
  const [billingPortalError, setBillingPortalError] = useState('')

  if (!isOpen) return null

  const handleUpdatePayment = async () => {
    try {
      setBillingPortalError('')
      setIsOpeningBillingPortal(true)

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY
      const accessToken = getStoredSupabaseAccessToken()

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Billing portal is not configured. Missing Supabase environment variables.')
      }

      if (!accessToken) {
        throw new Error('Your session has expired. Please log in again and retry.')
      }

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), BILLING_PORTAL_TIMEOUT_MS)
      const response = await fetch(`${supabaseUrl}/functions/v1/create-billing-portal-session`, {
        method: 'POST',
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      }).finally(() => clearTimeout(timeoutId))

      const data = await response.json().catch(() => null)

      if (!response.ok) {
        throw new Error(data?.error || `Unable to open billing portal. Server returned ${response.status}.`)
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      if (!data?.url) {
        throw new Error('Billing portal URL was not returned by the server.')
      }

      window.location.href = data.url
    } catch (error) {
      const message = error?.name === 'AbortError'
        ? 'Billing portal request timed out. Please try again.'
        : error?.message || 'Unable to open billing portal. Please try again.'
      console.error('Payment issue billing portal error:', message)
      setBillingPortalError(message)
      setIsOpeningBillingPortal(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl">
        <div className="recipe-app-shell subscription-modal-header bg-recipe-gradient p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">
                Payment Method Issue
              </h2>
              <p className="text-white/90 text-sm">
                Action needed to keep Premium access
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              aria-label="Dismiss payment issue notice"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <Card className="border-2 border-amber-200 bg-amber-50/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl text-gray-900">Update your payment information</CardTitle>
              <CardDescription className="text-base text-gray-700 mt-3 leading-relaxed">
                {PAYMENT_ISSUE_MODAL_COPY}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              {billingPortalError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {billingPortalError}
                </div>
              )}

              <Button
                onClick={handleUpdatePayment}
                disabled={isOpeningBillingPortal}
                className="nav-get-started-lines w-full h-12 text-lg font-semibold"
              >
                <span className="relative z-10">
                  {isOpeningBillingPortal ? 'Opening Stripe...' : 'Update Payment Method'}
                </span>
              </Button>

              <Button
                onClick={onClose}
                variant="outline"
                className="w-full h-12 text-lg font-semibold"
              >
                Remind Me Later
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default PaymentIssueModal
